import { MouseEvent, MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Position } from 'css-box-model'
import { t } from 'i18next'
import moment from 'moment'
import { v4 as uuid } from 'uuid'
import { MuiEvent } from '@microservices/wiskey-react-table-component'
import { RowData } from '@tanstack/react-table'

import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { CursorPosition, HandlerData, MetaData } from '@components/NestedMenu'
import { VariableValue } from '@components/Variables/types'

import { useExecuteCommandByIdMutation } from '@redux/api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { setAvailableGantt } from '@redux/reducers/availableGantts.reducer'
import {
  openFormDialog,
  openSectionalWindowDialog,
  openViewDialog,
} from '@redux/reducers/dialogWindowManager.reducer'
import { setDialogPending } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'
import { showMessage } from '@redux/reducers/snackbar.reducer'

import {
  assignPriorityAndSortActions,
  findReservedVariable,
  getCommandRequestData,
  getInitialPositionWindow,
  getOptionRoute,
  ReturnTransformCommonParametersToValues,
  transformCommonParametersToValues,
} from '@helpers'
import { getDialogWindowsFromReduxStore } from '@helpers'
import {
  ACTION_CODE,
  COMMON_PARAMETERS,
  ENTITY,
  FORM_TYPE,
  OBJECT_TYPES_FOR_MENU_POINT_VALUE,
  RESERVED_VARIABLES_NAMES,
} from '@constants'
import { ActionRow, DIALOG_WINDOW_TYPE, MenuPointOptionType, TimeLineGridColumn } from '@types'

import { useAppDispatch } from './redux'

type TSrcObj = {
  rowData: RowData
  columnData: TimeLineGridColumn
  columnId: string | null
  rowId: string
  startDate: string
  endDate: string
  variables?: VariableValue[]
  formProps: {
    objectId?: string
  }
}

type TExecuteAction = {
  action: ActionRow
  event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>
  srcObj: TSrcObj
}

const positionOffset = 1
const reversePositionOffset = 2

export const useContextMenu = ({
  dialogId,
  variantPosition = 'cursor',
}: {
  dialogId?: string
  variantPosition?: 'cursor' | 'element'
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [position, setPosition] = useState<CursorPosition | null>(null)
  const refArrayVariables = useRef<VariableValue[]>([])

  const handleChangePosition = useCallback(
    (node: HTMLElement | null) => {
      if (node === null || position === null) return

      const { innerHeight, innerWidth } = window
      const rect = node.getBoundingClientRect()

      let top = position.top
      let left = position.left

      if (top + rect.height > innerHeight) {
        top = position.top - rect.height
        if (top < 0) top = 0
      }

      if (left + rect.width > innerWidth) {
        left = position.left - rect.width
        if (left < 0) left = 0
      }

      if (top !== position.top || left !== position.left) {
        setPosition({ top, left })
      }
    },
    [position]
  )

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const [executeCommandById] = useExecuteCommandByIdMutation()

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const checkMaxOpenedFormsCount = (
    initialCommonParameters: ReturnTransformCommonParametersToValues
  ) => {
    const dialogWindows = getDialogWindowsFromReduxStore()

    const openedFormsCount = dialogWindows.filter(
      dialog =>
        [
          DIALOG_WINDOW_TYPE.VIEW,
          DIALOG_WINDOW_TYPE.FORM,
          DIALOG_WINDOW_TYPE.SECTIONAL_WINDOW,
        ].includes(dialog.type) && !dialog.hidden
    ).length

    const maxOpenedFormsCount = Number(initialCommonParameters.numberOfWindows)

    if (openedFormsCount === maxOpenedFormsCount) {
      dispatch(
        showMessage({
          type: 'info',
          text: t('error.dynamicFormsExceeded', { count: maxOpenedFormsCount }),
        })
      )

      return true
    }

    return false
  }

  const handleOpenContextMenu = (event: MouseEvent<HTMLElement>) => {
    switch (variantPosition) {
      case 'cursor':
        setPosition({ left: event.clientX, top: event.clientY })
        break
      case 'element': {
        const { left, bottom } = event.currentTarget.getBoundingClientRect()
        setPosition({ left: left - 4, top: bottom + 3 })

        return
      }
      default:
        setPosition({ left: event.clientX, top: event.clientY })
    }
  }

  const handleCloseContextMenu = () => setPosition(null)

  const handleOpenView = (item: MenuPointOptionType, position?: Position) => {
    if (item.value && !checkMaxOpenedFormsCount(initialCommonParameters)) {
      dispatch(
        openViewDialog({
          id: uuid(),
          parentDialogId: dialogId || null,
          type: DIALOG_WINDOW_TYPE.VIEW,
          title: item.title,
          meta: {
            viewId: Number(item.value),
            viewCode: item.valueCode ?? item.value ?? null,
            title: item.valueTitle || '',
            path: item.id.toString(),
            menuId: item.id,
          },
          initialPosition: position,
        })
      )
    }
  }

  function handleOpenForm(
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    additionalProps?: {
      addHistory?: boolean
    }
  ) {
    if (checkMaxOpenedFormsCount(initialCommonParameters)) return

    const { id } = formProps

    const globalId = uuid()

    const initialPosition = getInitialPositionWindow(event)

    if (additionalProps?.addHistory) {
      dispatch(
        addFormHistory({
          id: id || globalId,
          form: {
            ...formProps,
            isWindow: true,
            title: formProps.formCode,
            objectId: formProps.objectId || null,
            id: id || globalId,
          },
        })
      )
    }

    dispatch(
      openFormDialog({
        id: id || globalId,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          ...formProps,
          globalId: id || globalId,
        },
        initialPosition,
      })
    )
  }

  const handleSetVariableValue = (
    action: ActionRow,
    refArrayVariables: MutableRefObject<VariableValue[]>,
    srcObj: TSrcObj
  ) => {
    const serializeValue = value => {
      if (moment.isMoment(value) || value instanceof Date) {
        return value.toISOString()
      }

      return value
    }

    const indexChangedVariable = refArrayVariables.current.findIndex(
      variable => variable.id === action?.variable?.id
    )

    if (indexChangedVariable !== -1 && action.jsValue) {
      const newValue = new Function('srcObj', action.jsValue)(srcObj)

      refArrayVariables.current = refArrayVariables.current.map((variable, index) =>
        index === indexChangedVariable
          ? { ...variable, defaultValue: serializeValue(newValue) }
          : variable
      )
    }
  }

  // TODO Костыль с action в context menu (открытие формы в режиме редактирования)
  const executeAction = async ({ action, event, srcObj }: TExecuteAction) => {
    const valueVariableFormObjectId = findReservedVariable(
      refArrayVariables.current,
      RESERVED_VARIABLES_NAMES.FORM_OBJECT_ID
    )?.defaultValue

    // TODO Тоже костыль, идёт привязка к наименованию переменных для работы pre fill
    const preFillVariableFormObjectId = findReservedVariable(
      refArrayVariables.current,
      RESERVED_VARIABLES_NAMES.PREFILL_OBJECT_ID
    )?.defaultValue
    const preFillVariableFormObjectCode = findReservedVariable(
      refArrayVariables.current,
      RESERVED_VARIABLES_NAMES.PREFILL_OBJECT_CODE
    )?.defaultValue

    const hasPreFillByVariables = Boolean(
      preFillVariableFormObjectCode && preFillVariableFormObjectId
    )

    const formProps: OpenFormProps = {
      preFill: hasPreFillByVariables
        ? {
            objectCode: preFillVariableFormObjectCode,
            objectId: preFillVariableFormObjectId,
          }
        : undefined,
      objectCode: action.objectCode ?? '',
      variables: [...refArrayVariables.current],
      formCode: action.objectFormCode ?? '',
      viewCode: '',
      objectId: valueVariableFormObjectId ?? '',
      path: action.objectFormCode ?? '',
      event: valueVariableFormObjectId ? FORM_TYPE.EDIT : FORM_TYPE.CREATE,
      globalId: action.id,
      entityType: ENTITY.VIEW,
      title: '',
      id: '',
      isIndependentForm: true,
      outerData: {
        commands: action.redefinitionCommands || [],
        srcObj: {
          ...srcObj,
          outerVariables: [...refArrayVariables.current],
        },
      },
    }

    switch (action.actionCode) {
      case ACTION_CODE.OPEN_ANOTHER_FORM_MODEL:
      case ACTION_CODE.OPEN_FORM_MODEL: {
        handleOpenForm(formProps, event, {
          addHistory: true,
        })

        break
      }

      case ACTION_CODE.SET_VARIABLE_VALUE: {
        handleSetVariableValue(action, refArrayVariables, srcObj)

        break
      }
    }
  }

  const handleOpenGanttChart = (item: MenuPointOptionType) => {
    if (item.value) {
      const path = getOptionRoute(item)

      dispatch(
        setAvailableGantt({
          id: Number(item.value),
          title: item.valueTitle || item.title,
          path,
        })
      )

      navigate('/' + path)
    }
  }

  const handleOpenWindow = (item: MenuPointOptionType, position?: Position) => {
    if (item.value && !checkMaxOpenedFormsCount(initialCommonParameters)) {
      const id = uuid()

      dispatch(
        openSectionalWindowDialog({
          id,
          parentDialogId: null,
          type: DIALOG_WINDOW_TYPE.SECTIONAL_WINDOW,
          title: item.valueTitle ?? '',
          initialPosition: position,
          meta: {
            id: String(item.value),
          },
        })
      )
    }
  }

  const handleClickLink = (item: MenuPointOptionType, position?: Position) => {
    switch (item.objectType) {
      case OBJECT_TYPES_FOR_MENU_POINT_VALUE.VIEW:
        handleOpenView(item, position)
        break
      case OBJECT_TYPES_FOR_MENU_POINT_VALUE.GANTT_CHART:
        handleOpenGanttChart(item)
        break
      case OBJECT_TYPES_FOR_MENU_POINT_VALUE.WINDOW:
        handleOpenWindow(item, position)
    }
  }

  const handleExecuteCommand = (data: any) => {
    dispatch(setDialogPending({ id: String(data.menuItem.id), isLoading: true }))
    executeCommandById({
      commandId: data.menuItem.value,
      body: getCommandRequestData(data.menuItem.commandBody, data.srcObj, undefined, undefined, {
        outerSrcObj: data.outerSrcObj,
      }),
      params: getCommandRequestData(
        data.menuItem.commandParameters,
        data.srcObj,
        undefined,
        undefined,
        { outerSrcObj: data.outerSrcObj }
      ),
    }).then(() => dispatch(setDialogPending({ id: String(data.menuItem.id), isLoading: false })))
  }

  const handleExecuteAction = (data: MetaData & HandlerData) => {
    if (refArrayVariables.current.length === 0) {
      const variables = data?.srcObj?.variables ?? []
      const outerVariablesForInnerAction = data?.srcObj?.outerVariablesForInnerAction ?? []

      const valueArrayVariables = variables.length === 0 ? outerVariablesForInnerAction : variables

      refArrayVariables.current = [...valueArrayVariables]
    }

    const allActions = assignPriorityAndSortActions(data.menuItem.actions)

    const executeSequentially = async () => {
      for (const action of allActions) {
        await executeAction({
          action,
          event: data.event,
          srcObj: data.srcObj,
        })
      }
    }

    executeSequentially()
  }

  return {
    position,
    onRefContextMenu: handleChangePosition,
    onOpenContextMenu: handleOpenContextMenu,
    onClickLink: handleClickLink,
    onExecuteCommand: handleExecuteCommand,
    onCloseContextMenu: handleCloseContextMenu,
    onExecuteAction: handleExecuteAction,
  }
}
